import { SwapVert } from '@mui/icons-material'
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material'
import {
  SortOrderEnum,
  UserSortColumnEnum,
} from '@ulysses-inc/harami_api_client'
import React, { useState } from 'react'
import { DEFAULT_USERS_SORT_ORDER, UsersSortOrder } from './usersSortOrder'

interface OwnProps {
  getSortedUsers: (
    isClear: boolean,
    sortColumn: UserSortColumnEnum | undefined,
    sortOrder: SortOrderEnum,
  ) => void
  usersSortOrder: UsersSortOrder
}

type SortDisplayValue = {
  name: string
  asc: string
  desc: string
  /** 並び替え列を選び直したときに適用する方向。API 側の既定方向と揃える */
  defaultSortOrder: SortOrderEnum
}

const sortDisplay: Record<UserSortColumnEnum, SortDisplayValue> = {
  [UserSortColumnEnum.CreatedAt]: {
    name: '追加日時',
    asc: '古い順',
    desc: '新しい順',
    defaultSortOrder: SortOrderEnum.Desc,
  },
  // 管理番号は MySQL の照合順序 (utf8mb4_general_ci) 順のため「昇順 / 降順」と表記する
  [UserSortColumnEnum.Code]: {
    name: 'ユーザー管理番号',
    asc: '昇順',
    desc: '降順',
    defaultSortOrder: SortOrderEnum.Asc,
  },
}

const isUserSortColumn = (value: string): value is UserSortColumnEnum =>
  (Object.values(UserSortColumnEnum) as string[]).includes(value)

const UsersSortDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  // localStorage 経由で想定外の値が混入し得るため、ランタイムでも検証する。
  // 不正値の場合はクリア相当として既定値 (追加日時 DESC) にフォールバック。
  const rawSortColumn = props.usersSortOrder.sortColumn
  const validSortColumn = isUserSortColumn(rawSortColumn)
    ? rawSortColumn
    : DEFAULT_USERS_SORT_ORDER.sortColumn
  const sortOrder = props.usersSortOrder.sortOrder

  const changeSortColumn = (value: string) => {
    if (!isUserSortColumn(value)) return
    props.getSortedUsers(false, value, sortDisplay[value].defaultSortOrder)
  }

  const clearSort = () => {
    // 呼び出し側で既定値 (追加日時 DESC) に戻す
    props.getSortedUsers(true, undefined, DEFAULT_USERS_SORT_ORDER.sortOrder)
  }

  const changeSortOrder = (value: SortOrderEnum) => {
    props.getSortedUsers(false, validSortColumn, value)
  }

  const currentDisplay = sortDisplay[validSortColumn]
  const buttonLabel = `並び替え: ${currentDisplay.name}(${
    sortOrder === SortOrderEnum.Asc ? currentDisplay.asc : currentDisplay.desc
  })`

  const hoverArea = (
    <Stack sx={{ p: 2 }} spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={clearSort}>クリア</Button>
      </Box>
      <Select
        fullWidth
        size="small"
        value={validSortColumn}
        inputProps={{ 'aria-label': '並び替え列' }}
        onChange={(e: SelectChangeEvent<string>) =>
          changeSortColumn(e.target.value)
        }
      >
        {(Object.keys(sortDisplay) as UserSortColumnEnum[]).map(key => (
          <MenuItem key={key} value={key}>
            {sortDisplay[key].name}
          </MenuItem>
        ))}
      </Select>
      <RadioGroup
        value={sortOrder}
        onChange={e => changeSortOrder(e.target.value as SortOrderEnum)}
        sx={{ mt: 1.5 }}
      >
        <FormControlLabel
          value={SortOrderEnum.Desc}
          control={<Radio size="small" />}
          label={currentDisplay.desc}
        />
        <FormControlLabel
          value={SortOrderEnum.Asc}
          control={<Radio size="small" />}
          label={currentDisplay.asc}
        />
      </RadioGroup>
    </Stack>
  )

  return (
    <Box>
      <Button
        color="secondary"
        variant="outlined"
        startIcon={<SwapVert />}
        onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      >
        {buttonLabel}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { width: '100%', maxWidth: 232 },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </Box>
  )
}

export default UsersSortDropDown
